import { useEffect, useState } from "react";
import { getCompanySettings, setHostDirectEntry, updateCompanySettings } from "./api";
import { useAdminResource } from "./use-admin-resource";
import { useSubscription } from "./useSubscription";
import { LockedFeature } from "./LockedFeature";
import { useToast } from "../../components/useToast";

export function SlaSettingsPanel() {
  const { data: settings, loading, error, refresh } = useAdminResource(
    () => getCompanySettings(),
    [],
  );
  const { data: subscription, loading: subscriptionLoading } = useSubscription();
  const { showSuccess, showError } = useToast();

  const [warningMinutes, setWarningMinutes] = useState("60");
  const [criticalMinutes, setCriticalMinutes] = useState("120");
  const [savePending, setSavePending] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [directEntry, setDirectEntry] = useState(false);
  const [directPending, setDirectPending] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setWarningMinutes(String(settings.approval_sla_warning_minutes));
    setCriticalMinutes(String(settings.approval_sla_critical_minutes));
    setDirectEntry(!!settings.allow_host_direct_entry);
  }, [settings]);

  async function toggleDirectEntry(next: boolean) {
    setDirectEntry(next);
    setDirectPending(true);
    try {
      await setHostDirectEntry(next);
      showSuccess(next ? "Hosts can now issue direct-entry passes" : "Host direct entry disabled");
      await refresh();
    } catch (requestError) {
      setDirectEntry(!next);
      showError(requestError instanceof Error ? requestError.message : "Failed to update setting");
    } finally {
      setDirectPending(false);
    }
  }

  const warningValue = Number(warningMinutes);
  const criticalValue = Number(criticalMinutes);
  const isValid =
    Number.isInteger(warningValue) &&
    Number.isInteger(criticalValue) &&
    warningValue >= 1 &&
    criticalValue > warningValue &&
    criticalValue <= 1440;

  async function submitSave() {
    if (!isValid) {
      setSaveError("Critical threshold must be greater than the warning threshold.");
      return;
    }
    setSavePending(true);
    setSaveError(null);
    try {
      await updateCompanySettings({
        approval_sla_warning_minutes: warningValue,
        approval_sla_critical_minutes: criticalValue,
      });
      showSuccess("Saved approval SLA thresholds");
      await refresh();
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Failed to save settings";
      setSaveError(message);
      showError(message);
    } finally {
      setSavePending(false);
    }
  }

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Approval SLA Settings</h2>
        <span>Queue Aging</span>
      </header>
      <p className="management-note">
        Set the wait-time thresholds that color-code the approvals queue on the dashboard.
        Visits waiting past the warning threshold are flagged; past the critical threshold
        they're treated as most urgent.
      </p>

      {error ? <p className="error-banner">{error}</p> : null}

      <LockedFeature
        feature="approval_sla"
        featureLabel="Approval SLA configuration"
        subscription={subscription}
        loading={subscriptionLoading}
      >
        <form
          className="action-form action-card"
          onSubmit={(event) => {
            event.preventDefault();
            void submitSave();
          }}
        >
          <label htmlFor="sla-warning-minutes">Approval SLA Thresholds</label>
          <div className="checkin-fields">
            <input
              id="sla-warning-minutes"
              type="number"
              min={1}
              max={1440}
              value={warningMinutes}
              onChange={(event) => setWarningMinutes(event.target.value)}
              placeholder="Warning (minutes)"
              aria-label="Warning threshold in minutes"
              disabled={loading}
            />
            <input
              id="sla-critical-minutes"
              type="number"
              min={1}
              max={1440}
              value={criticalMinutes}
              onChange={(event) => setCriticalMinutes(event.target.value)}
              placeholder="Critical (minutes)"
              aria-label="Critical threshold in minutes"
              disabled={loading}
            />
          </div>
          <button className="refresh-btn action-submit" type="submit" disabled={savePending || loading}>
            {savePending ? "Saving..." : "Save Thresholds"}
          </button>
        </form>
      </LockedFeature>
      {saveError ? <p className="error-banner action-error">{saveError}</p> : null}

      <div className="action-card" style={{ marginTop: "1rem" }}>
        <h3 style={{ margin: "0 0 0.4rem" }}>Host direct entry</h3>
        <p className="management-note" style={{ marginTop: 0 }}>
          When on, a host can issue a ready-to-use gate pass by entering the visitor's name — the
          visitor gets the pass QR by email and skips the detail form (no separate approval).
        </p>
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={directEntry}
            disabled={directPending || loading}
            onChange={(event) => void toggleDirectEntry(event.target.checked)}
          />
          Allow hosts to issue direct-entry passes
        </label>
      </div>
    </article>
  );
}
